// Imports ------------------------------------------------------------------->
import {Component, attr} from "../Component";
import {element} from "../CustomElement";
import {i18n, ll} from "../../i18n/I18n";
import * as array from "../../utils/array";
import {parseSearchParams} from "../../utils/URL";

import {CouponElement} from "../flipp-coupon/CouponElement";

import {inject} from "../../services/di";
import {normalizeDate} from "../../utils/date";
import {CouponService} from "../../services/CouponService";
import {LoyaltyProgramService} from "../../services/LoyaltyProgramService";
import {LocationService} from "../../services/LocationService";
import {FlyerService} from "../../services/FlyerService";
import {UserAgentService} from "../../services/UserAgentService";
import {AnalyticsService} from "../../services/AnalyticsService";
import {SeoService} from "../../services/SeoService";

import {Flyer, FlyerItemDetails} from "../../definitions/FlyerDefinitions";
import {Coupon, CouponRedemptionMethod} from "../../definitions/CouponDefinitions";
import {DisplayType} from "../../definitions/BackflippDefinitions";

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

interface FlyerItemElementAttributes {
	itemId: number;
}

type FlyerItemModel = [FlyerItemDetails, Flyer, Coupon[], number, string];

@element("flipp-flyer-item")
export class FlyerItemElement extends Component<FlyerItemElementAttributes, FlyerItemModel> {
	@attr itemId: number;
	@inject location: LocationService;
	@inject flyerService: FlyerService;
	@inject couponService: CouponService;
	@inject loyaltyService: LoyaltyProgramService;
	@inject userAgent: UserAgentService;
	@inject analytics: AnalyticsService;
	@inject seoService: SeoService;

	onAttach() {
		window.addEventListener("flipp-auth", this.handleAuthEvent_);
		window.addEventListener("flipp-couponclipped", this.handleItemClipped_);
	}

	onDetach() {
		window.removeEventListener("flipp-auth", this.handleAuthEvent_);
		window.removeEventListener("flipp-couponclipped", this.handleItemClipped_);
	}

	prepare() {
		return Promise.all([
			this.flyerService.getFlyerItem(this.itemId),
			this.couponService.getCouponsForFlyerItem(this.itemId),
			this.loyaltyService.getLoyaltyProgramMerchants()
		]).then((results) => {
			// Need the flyer id to request the flyer :(
			const [item, coupons, programMerchants] = results;

			// FIXME [FB-765] video_url is only passed through the flyer details.
			if (item.displayType === DisplayType.VIDEO) {
				return Promise.all([
					this.flyerService.getFlyer(item.flyerId),
					this.flyerService.getFlyerDetails(item.flyerId)
				]).then(([flyer, details]) => {
					const itemDetails = array.find(details.items, (detail) =>
							detail.id === item.id);
					const matchingProgram = array.find(programMerchants,
						(p) => p.merchantId === flyer.merchantId);
					const matchingId = matchingProgram ? matchingProgram.id : -1;
					return [item, flyer, coupons, matchingId, itemDetails.videoUrl] as FlyerItemModel;
				});
			} else {
				return this.flyerService.getFlyer(item.flyerId).then((flyer) => {
					const matchingProgram = array.find(programMerchants,
						(p) => p.merchantId === flyer.merchantId);
					const matchingId = matchingProgram ? matchingProgram.loyaltyProgramId : -1;
					return [item, flyer, coupons, matchingId, null] as FlyerItemModel;
				});
			}
		});
	}

	render(model: FlyerItemModel) {
		const [item] = model;

		switch (item.displayType) {
			case DisplayType.LINK:
				return this._renderLink(model);
			case DisplayType.IFRAME:
			case DisplayType.ONLYFEATUREHTML:
				return this._renderIframe(model);
			case DisplayType.VIDEO:
				return this._renderVideo(model);
			default :
				return this._renderItem(model);
		}
	}

	private _renderVideo([ , , , , videoUrl]: FlyerItemModel) {
		return <div class="video">
			{ /youtube.com\/embed/.test(videoUrl) ?
				<iframe src={videoUrl} frameborder={0} allowfullscreen></iframe>
			:
				<video autoplay controls src={videoUrl}></video>
			}
		</div>;
	}

	private _renderIframe([item, flyer]: FlyerItemModel) {
		const ttmUrl = this._makeTTM(item);
		const baseUrl = new URL(ttmUrl);

		// We need to add DCv2 parameters
		const DCv2params: {[key: string]: any} = {
			flipp_store_code: null as void,
			flipp_locale: this.seoService.locale,
			flipp_flyer_id: flyer.id,
			flipp_flyer_run_id: flyer.flyerRunId,
			flipp_merchant_id: flyer.merchantId,
			flipp_flyer_type_id: flyer.flyerTypeId,
			flipp_premium_merchant: flyer.premium,
			flipp_aid: "flipp",
			flipp_sid: this.analytics.sessionId,
			flipp_channel_id: this.analytics.channelId,
			flipp_ciab: false,
			flipp_client_type: 1,      // ?
			flipp_location_source: 1,  // ?
			flipp_postal_code: this.location.postalCode
		};

		const searchParams = parseSearchParams(baseUrl);
		const qs = "?" + [].concat(
				Object.getOwnPropertyNames(searchParams),
				Object.getOwnPropertyNames(DCv2params)
		).map((key) => {
			const v = String(key in DCv2params ? DCv2params[key] : searchParams[key]);
			return `${key}=${encodeURIComponent(v)}`;
		}).join("&");

		const url = `${baseUrl.protocol}//${baseUrl.host}${baseUrl.pathname}${qs}${baseUrl.hash}`;

		// return <iframe width={item.iframeDisplayWidth}
		//                height={item.iframeDisplayHeight + 44}
		//                src={url}></iframe>;
		return <div class="iframe">
				<iframe class="dcv2-content" src={url} frameborder={0}></iframe>
			</div>;
	}

	private _renderLink([item]: FlyerItemModel) {
		const ttmUrl = this._makeTTM(item);
		const ttmUrlResultLink = ttmUrl === null
			?
			<a target="_blank">
				{i18n("click_to_visit_m", { m: item.merchant })}
			</a>
			:
			<a target="_blank" href={ttmUrl}>
				{i18n("click_to_visit_m", { m: item.merchant })}
			</a>;

		return <div class="link">
			{ttmUrlResultLink}
		</div>;
	}

	private _renderItem([item, flyer, coupons, loyaltyProgramId]: FlyerItemModel) {
		const flyerDisclaimer = item.flyerDisclaimerText ?
				<div class="pop-disclaimer">{item.flyerDisclaimerText}</div> :
				null;

		const inStoreOnly = item.inStoreOnly ?
				<div class="sale-story">{i18n("in_store_only")}</div> : null;
		const saleStory = item.saleStory ?
				<div class="sale-story">{item.saleStory}</div> : null;
		const description = item.description ?
			<div>
				<h3>{i18n("description")}</h3>
				<span>{item.description}</span>
			</div>
			: null;

		const sku = item.sku ?
				<div class="flyer-item-sku">SKU: {item.sku}</div> : null;

		const couponsEl = coupons.length > 0 ?
				<section class="coupons">
					<h3>{i18n("matching_coupons")}</h3>
					<ul>
						{coupons.map((coupon) =>
								<CouponElement
									id={`coupon-element-${coupon.id}`}
									couponId={coupon.id}
									scopedProgramId={loyaltyProgramId}
									flyerContext={item.flyerId} />
						)}
					</ul>
				</section> : null;

		const features = item.features.length > 0 ?
				<section class="features">
					<h3>{i18n("features")}</h3>
					<ul>
						{item.features.map((feature) => {
							return <li>{feature.text}</li>;
						})}
					</ul>
				</section> : null;

		const specs = item.specs.length > 0 ?
				<section class="specs">
					<h3>{i18n("specs")}</h3>
					<ul>
						{item.specs.map((spec) => {
							return <li>
								<span class="spec-name">{spec.name}:</span>
								<span class="spec-value">{spec.value}</span>
							</li>;
						})}
					</ul>
				</section> : null;

		const reviews = item.reviewSamples.length > 0 ?
				<section class="reviews">
					<h3>{i18n("reviews")}</h3>
					<div class="review-summary">
						{i18n("overall_rating")}
						<flipp-rating value={item.reviewAverageRating.toString()}/>
					</div>
					<ul class="clearfix">
						{item.reviewSamples.map((review) => {
							return <li>
								<div class="review">
									<div class="title-area">
										<div class="byline">
											{review.rating ? <flipp-rating value={review.rating.toString()}/> : null}
											<div class="title">{review.title}</div>
											<div>
												<span class="author">{i18n("review_by_author", {
													author: review.byline
												})}</span>
												{/* Make sure that 'review.date' is defined, because, otherwise, Jan 1 1970 is returned. */}
												{review.date && <span class="date">{ll(new Date(review.date))}</span>}
											</div>
										</div>
									</div>

									<div class="body">{review.body}</div>
								</div>
							</li>;
						})}
					</ul>
				</section> : null;

		return <div classList={{
									webindexed: flyer.webIndexed && coupons.length === 0
								}}>
			<div class="container">
				<section>
					{inStoreOnly}
					{saleStory}
					<div class="flyer-item-validity">
						{i18n("valid_from_to", {
							from: ll(normalizeDate(item.validFrom)),
							to: ll(normalizeDate(item.validTo))
						})}
					</div>
					<div class="flyer-item-disclaimer-text">
						{item.disclaimerText}
					</div>
				</section>
				{ couponsEl }
				<section class="description">
					{description}
					{sku}
				</section>
				{features}
				{specs}
				{reviews}
				{flyerDisclaimer}
				<div class="pop-disclaimer">{i18n("item_disclaimer")}</div>
			</div>
		</div>;
	}

	private _makeTTM(item: FlyerItemDetails) {
		return item.ttmUrl ? item.ttmUrl
				.replace("__POSTAL_CODE__", this.location.postalCode)
				.replace("__FLIPP_POSTAL_CODE__", this.location.postalCode)
				.replace("__FLIPP_LOCALE__", this.location.locale) : null;
	}

	private handleAuthEvent_ = () => {
		this.invalidate();
	}

	private handleItemClipped_ = (e: CustomEvent) => {
		if (e.detail.action !== "added") return;
		if (e.detail.context !== "clip-button") return;

		const isBlocked = this.userAgent.mobile && screen.width <= 600;
		/* This breaks the component model a bit, but it's here so that the parent component (this)
			* can trigger clip events on all child coupon clipping components. */
		const [, , coupons] = this.model;
		coupons.forEach((coupon) => {
			if (isBlocked && coupon.redemptionMethod === CouponRedemptionMethod.SAVE_TO_CARD) {
				// Don't autoclip L2C coupons on mobile
				return;
			}
			const couponElement =
				(document.getElementById(`coupon-element-${coupon.id}`) as any) as CouponElement;
			couponElement.clip();
		});
	}
}
